import { t } from "@lingui/macro";
import { Button } from "@reactive-resume/ui";
import { useNavigate } from "react-router";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@reactive-resume/ui";
import { useAuthStore } from "@/client/stores/auth";
import { useState, useEffect } from "react";
import { usageLimitErrorAPI } from "@/client/libs/axios";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import { AlertOctagon, Clock, ArrowUpCircle } from "lucide-react";

// Initialize dayjs duration plugin
dayjs.extend(duration);

export interface UsageLimitErrorData {
  error: string;
  feature: string;
  resetIn: string;
}

export const UsageLimitDialog = () => {
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);
  const [error, setError] = useState<UsageLimitErrorData | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  // Listen for errors from the API
  useEffect(() => {
    // Check for existing error
    const currentError = usageLimitErrorAPI.getCurrentError();
    if (currentError) {
      setError(currentError);
      setIsOpen(true);
    }

    // Add listener for future errors
    const removeListener = usageLimitErrorAPI.addListener((newError) => {
      setError(newError);
      setIsOpen(true);
    });

    return () => {
      removeListener();
    };
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const formatResetTime = (resetIn: string) => {
    // Assuming resetIn is in a format like "2h 30m" or "45m" or "1d 3h 45m"
    try {
      // Parse the time string into a duration
      const timeUnits = resetIn.match(/(\d+)([dhms])/g) || [];
      let seconds = 0;

      timeUnits.forEach((unit) => {
        const value = parseInt(unit);
        const type = unit.slice(-1);

        if (type === "d") seconds += value * 86400;
        if (type === "h") seconds += value * 3600;
        if (type === "m") seconds += value * 60;
        if (type === "s") seconds += value;
      });

      const duration = dayjs.duration(seconds, "seconds");

      // Format the duration in a human-readable way
      if (duration.asHours() >= 24) {
        return duration.format("D [days], H [hours], m [minutes]");
      } else if (duration.asHours() >= 1) {
        return duration.format("H [hours], m [minutes]");
      } else {
        return duration.format("m [minutes]");
      }
    } catch (e) {
      // If parsing fails, return the original string
      return resetIn;
    }
  };

  const getFeatureDescription = () => {
    let feature = error?.feature;

    switch (feature) {
      case "RESUME_PARSING":
        feature = t`creating a new resume`;
        break;
      case "RESUME_OPTIMIZATION":
        feature = t`optimizing a resume`;
        break;
      case "RESUME_ANALYSIS":
        feature = t`analyzing a resume`;
        break;
      default:
        feature = t`this feature`;
    }

    return feature;
  };

  const getDescription = () => {
    const feature = getFeatureDescription();

    let desc = t`You've reached your usage cap for ${feature}.`;
    if (user && !user.plusUser) {
      desc += t` You can upgrade to Plus for higher usage limits.`;
    }
    return desc;
  };

  if (!error) return null;

  const formattedTime = error?.resetIn ? formatResetTime(error.resetIn) : error?.resetIn;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-3">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
            <AlertOctagon className="h-6 w-6 text-amber-600 dark:text-amber-500" />
          </div>
          <DialogTitle className="text-center text-xl">{t`Usage Limit Reached`}</DialogTitle>
          <DialogDescription className="text-center">{getDescription()}</DialogDescription>
        </DialogHeader>

        <div className="bg-muted my-4 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-2 text-center">
            <Clock className="text-muted-foreground h-5 w-5" />
            <p className="font-medium">
              {t`Available again in:`} <span className="text-primary">{formattedTime}</span>
            </p>
          </div>
        </div>

        <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          <Button variant="outline" onClick={handleClose}>
            {t`Close`}
          </Button>
          {user && !user.plusUser && (
            <Button
              onClick={() => {
                navigate("/dashboard/upgrade");
                handleClose();
              }}
              className="gap-2"
            >
              <ArrowUpCircle className="h-4 w-4" />
              {t`Upgrade to Plus`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
